import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { ClipboardList, CheckCircle, Calendar, User, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MinhasAvaliacoes360 = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState(null);
  const [respostas, setRespostas] = useState({});

  useEffect(() => {
    carregarAvaliacoes();
  }, []);

  const carregarAvaliacoes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/minhas-avaliacoes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvaliacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast.error('Erro ao carregar avaliações pendentes');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAvaliacao = (avaliacao) => {
    setAvaliacaoSelecionada(avaliacao);
    // Inicializar respostas vazias (null para escala, string vazia para texto livre)
    const respostasIniciais = {};
    avaliacao.perguntas.forEach((pergunta, index) => {
      respostasIniciais[index] = pergunta.tipo_resposta === 'texto_livre' ? '' : null;
    });
    setRespostas(respostasIniciais);
  };

  const handleRespostaChange = (index, nota) => {
    setRespostas(prev => ({
      ...prev,
      [index]: nota
    }));
  };

  const submeterAvaliacao = async () => {
    // Validar se todas as perguntas foram respondidas
    const todasRespondidas = avaliacaoSelecionada.perguntas.every((pergunta, index) => {
      const resposta = respostas[index];
      // Para perguntas de texto livre, verificar se não está vazio
      if (pergunta.tipo_resposta === 'texto_livre') {
        return resposta && resposta.trim().length > 0;
      }
      // Para perguntas de escala, verificar se foi selecionada uma nota
      return resposta !== null && resposta !== undefined;
    });

    if (!todasRespondidas) {
      toast.error('Por favor, responda todas as perguntas antes de enviar');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Preparar respostas no formato correto
      const respostasFormatadas = avaliacaoSelecionada.perguntas.map((pergunta, index) => ({
        pergunta_index: index,
        pergunta: pergunta.pergunta,
        competencia: pergunta.competencia,
        resposta: respostas[index]
      }));

      await axios.post(
        `${API}/api/feedback360/responder/${avaliacaoSelecionada.id}`,
        { respostas: respostasFormatadas },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Avaliação enviada com sucesso!');
      setAvaliacaoSelecionada(null);
      setRespostas({});
      carregarAvaliacoes();
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      toast.error('Erro ao enviar avaliação');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !avaliacaoSelecionada) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando avaliações...</p>
        </div>
      </div>
    );
  }

  // Se está respondendo uma avaliação
  if (avaliacaoSelecionada) {
    const tipoTexto = {
      'autoavaliacao': 'Auto-avaliação',
      'par': 'Avaliação de Par',
      'cliente': 'Avaliação de Cliente Interno',
      'subordinado': 'Avaliação de Liderança'
    }[avaliacaoSelecionada.tipo_avaliador] || avaliacaoSelecionada.tipo_avaliador;

    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ClipboardList className="h-8 w-8 mr-3 text-purple-600" />
              {tipoTexto}
            </h1>
            <p className="text-gray-600 mt-1">
              {avaliacaoSelecionada.tipo_avaliador === 'autoavaliacao' 
                ? 'Avalie seu próprio desempenho'
                : `Avaliando: ${avaliacaoSelecionada.avaliado_nome}`
              }
            </p>
          </div>
          <Button variant="outline" onClick={() => setAvaliacaoSelecionada(null)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {avaliacaoSelecionada.perguntas.length} Perguntas
            </CardTitle>
            <p className="text-sm text-gray-600">
              Perguntas 1-{avaliacaoSelecionada.perguntas.length - 1}: Escala 1 (Nunca/Muito Fraco) a 5 (Sempre/Excelente)
              <br />
              Pergunta {avaliacaoSelecionada.perguntas.length}: Campo de texto livre (obrigatório)
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {avaliacaoSelecionada.perguntas.map((pergunta, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="mb-2">
                  <span className="font-semibold text-gray-900">
                    {index + 1}. {pergunta.pergunta}
                  </span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {pergunta.competencia}
                  </Badge>
                </div>
                
                {/* Verificar se é pergunta de texto livre (pergunta 21) */}
                {pergunta.tipo_resposta === 'texto_livre' ? (
                  <div className="mt-3">
                    <textarea
                      value={respostas[index] || ''}
                      onChange={(e) => handleRespostaChange(index, e.target.value)}
                      placeholder="Digite sua resposta aqui... (campo obrigatório)"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:ring-2 focus:ring-purple-200 transition-all min-h-[120px] resize-y"
                      required
                    />
                    {respostas[index] && (
                      <p className="text-xs text-gray-500 mt-1">
                        {respostas[index].length} caracteres
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2 mt-3">
                    {[1, 2, 3, 4, 5].map((nota) => (
                      <button
                        key={nota}
                        onClick={() => handleRespostaChange(index, nota)}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all font-semibold ${
                          respostas[index] === nota
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300 text-gray-600'
                        }`}
                      >
                        {nota}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setAvaliacaoSelecionada(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={submeterAvaliacao}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {loading ? 'Enviando...' : 'Enviar Avaliação'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Lista de avaliações pendentes
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <ClipboardList className="h-8 w-8 mr-3 text-purple-600" />
          Minhas Avaliações 360º
        </h1>
        <p className="text-gray-600 mt-1">
          Avaliações pendentes que você precisa responder
        </p>
      </div>

      {avaliacoes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhuma avaliação pendente
            </h3>
            <p className="text-gray-600">
              Você está em dia com suas avaliações 360º!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {avaliacoes.map((avaliacao) => {
            const tipoConfig = {
              'autoavaliacao': { label: 'Auto-avaliação', color: 'bg-blue-100 text-blue-700', icon: User },
              'par': { label: 'Avaliação de Par', color: 'bg-green-100 text-green-700', icon: User },
              'cliente': { label: 'Avaliação de Cliente', color: 'bg-purple-100 text-purple-700', icon: User },
              'subordinado': { label: 'Avaliação de Liderança', color: 'bg-orange-100 text-orange-700', icon: User }
            }[avaliacao.tipo_avaliador] || { label: avaliacao.tipo_avaliador, color: 'bg-gray-100 text-gray-700', icon: User };

            return (
              <Card key={avaliacao.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={tipoConfig.color}>
                          {tipoConfig.label}
                        </Badge>
                        <Badge variant="outline">
                          {avaliacao.perguntas.length} perguntas
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {avaliacao.ciclo_nome}
                      </h3>
                      {avaliacao.tipo_avaliador !== 'autoavaliacao' && (
                        <p className="text-gray-600 mb-2">
                          Avaliando: <span className="font-medium">{avaliacao.avaliado_nome}</span>
                        </p>
                      )}
                      {avaliacao.ciclo_data_fim && (
                        <p className="text-sm text-gray-500 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Prazo: {new Date(avaliacao.ciclo_data_fim).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => iniciarAvaliacao(avaliacao)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Responder
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MinhasAvaliacoes360;
